import React, { useEffect, useState, useCallback } from "react";
import { api, formatMoney, formatDate, formatDateTime, formatApiError } from "@/lib/api";
import { RefreshCw, Download, ChevronDown, ChevronRight, Save, X } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const OUTCOME_LABELS = {
    reached: "Ulaşıldı",
    no_answer: "Cevap yok",
    promised_payment: "Ödeme sözü",
    refused: "Reddetti",
    voicemail: "Sesli mesaj",
    other: "Diğer",
};

const OUTCOME_COLOR = {
    reached: "bg-blue-100 text-blue-800 border-blue-300",
    promised_payment: "bg-green-100 text-green-800 border-green-300",
    no_answer: "bg-neutral-100 text-neutral-600 border-neutral-300",
    refused: "bg-red-100 text-red-800 border-red-300",
    voicemail: "bg-amber-50 text-amber-700 border-amber-300",
    other: "bg-neutral-100 text-neutral-700 border-neutral-300",
};

export default function CollectionsBoard() {
    const [data, setData] = useState({ rows: [], totals: { customer_count: 0, total_debit_balance: 0, total_credit_balance: 0 } });
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("");
    const [expandedId, setExpandedId] = useState(null);
    const [exporting, setExporting] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get("/collections-board");
            setData(r.data);
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const filtered = data.rows.filter((r) => {
        if (!filter.trim()) return true;
        const q = filter.toLowerCase();
        return (
            r.code?.toLowerCase().includes(q) ||
            r.name?.toLowerCase().includes(q) ||
            r.last_note?.contact_person?.toLowerCase().includes(q)
        );
    });

    async function exportAll() {
        setExporting(true);
        try {
            const ids = filtered.map((r) => r.customer_id);
            const res = await api.post(
                "/customers/statements/bulk.xlsx",
                { customer_ids: ids },
                { responseType: "blob" }
            );
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a");
            a.href = url;
            a.download = "cari_hesap_ekstresi.xlsx";
            a.click();
            window.URL.revokeObjectURL(url);
            toast.success("Excel indirildi");
        } catch (err) {
            toast.error("İndirme başarısız");
        } finally {
            setExporting(false);
        }
    }

    return (
        <div>
            <header className="border-b border-black/10 px-10 py-6 flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <div className="overline">Bölüm 05</div>
                    <h1 className="font-display text-4xl font-black tracking-tight leading-none mt-1">
                        Tahsilat Ekranı
                    </h1>
                    <p className="text-sm text-neutral-500 mt-3 max-w-3xl">
                        Tüm müşterilerin güncel bakiyesi, atanan tahsildar ve son görüşme özeti tek ekranda. Satır başlığına tıklayarak hızlı not girişi yapabilirsiniz.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        data-testid="board-refresh"
                        onClick={load}
                        className="btn-outline"
                        disabled={loading}
                    >
                        <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Yenile
                    </button>
                    <button
                        data-testid="board-export"
                        onClick={exportAll}
                        className="btn-sharp"
                        disabled={exporting || filtered.length === 0}
                    >
                        <Download size={13} /> {exporting ? "…" : `Excel (${filtered.length})`}
                    </button>
                </div>
            </header>

            {/* Stats strip */}
            <div className="grid grid-cols-1 md:grid-cols-3 border-b border-black/10">
                <StatCell label="Müşteri" value={data.totals.customer_count} testid="board-stat-count" />
                <StatCell
                    label="Toplam Borç (Bize borçlu)"
                    value={`₺ ${formatMoney(data.totals.total_debit_balance)}`}
                    tone="debit"
                    testid="board-stat-debit"
                />
                <StatCell
                    label="Toplam Alacak"
                    value={`₺ ${formatMoney(data.totals.total_credit_balance)}`}
                    tone="credit"
                    testid="board-stat-credit"
                />
            </div>

            <div className="px-10 py-4 border-b border-black/10 flex items-center gap-3">
                <input
                    data-testid="board-search"
                    placeholder="Kod, müşteri veya görüşülen kişi ara…"
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    className="w-full max-w-md border border-black/20 px-3 py-2 text-sm font-mono focus:outline-none focus:border-brand-blue"
                />
                <span className="text-xs font-mono text-neutral-500">{filtered.length} kayıt</span>
            </div>

            <div className="px-10 py-6 overflow-x-auto">
                <table className="w-full text-sm" data-testid="board-table">
                    <thead>
                        <tr className="text-left border-b-2 border-brand-ink">
                            <th className="py-2 pr-2 w-6" />
                            <th className="py-2 pr-4 overline">Cari Kod</th>
                            <th className="py-2 pr-4 overline">Cari Ünvan</th>
                            <th className="py-2 pr-4 overline text-right">Bakiye</th>
                            <th className="py-2 pr-4 overline">Atanan Tahsildar</th>
                            <th className="py-2 pr-4 overline">Son Görüşme</th>
                            <th className="py-2 pr-4 overline">Söz</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr><td colSpan={7} className="py-8 text-center text-neutral-400 font-mono text-xs">Yükleniyor…</td></tr>
                        )}
                        {!loading && filtered.length === 0 && (
                            <tr><td colSpan={7} className="py-8 text-center text-neutral-400 font-mono text-xs">Kayıt yok</td></tr>
                        )}
                        {filtered.map((r) => {
                            const isOpen = expandedId === r.customer_id;
                            const n = r.last_note;
                            return (
                                <React.Fragment key={r.customer_id}>
                                    <tr
                                        className="border-b border-black/5 hover:bg-neutral-50 cursor-pointer"
                                        onClick={() => setExpandedId(isOpen ? null : r.customer_id)}
                                        data-testid={`board-row-${r.code}`}
                                    >
                                        <td className="py-3 pr-2 text-neutral-400">
                                            {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                        </td>
                                        <td className="py-3 pr-4 font-mono text-xs">{r.code}</td>
                                        <td className="py-3 pr-4">
                                            <Link
                                                onClick={(e) => e.stopPropagation()}
                                                to={`/musteriler/${r.customer_id}`}
                                                className="hover:underline"
                                            >
                                                {r.name}
                                            </Link>
                                        </td>
                                        <td className={`py-3 pr-4 text-right font-mono-num font-semibold ${r.balance > 0 ? "text-brand-red" : r.balance < 0 ? "text-brand-green" : "text-neutral-500"}`}>
                                            {r.balance > 0 ? "+" : ""}₺ {formatMoney(r.balance)}
                                        </td>
                                        <td className="py-3 pr-4 text-xs text-neutral-600">
                                            {r.assigned_collector_names.length ? r.assigned_collector_names.join(", ") : "—"}
                                        </td>
                                        <td className="py-3 pr-4 text-xs">
                                            {n ? (
                                                <div>
                                                    <div className="font-medium">{n.contact_person || "—"}</div>
                                                    <div className="text-neutral-500">{n.author_name} · {formatDateTime(n.created_at)}</div>
                                                </div>
                                            ) : <span className="text-neutral-400">—</span>}
                                        </td>
                                        <td className="py-3 pr-4 text-xs">
                                            {n?.call_outcome === "promised_payment" ? (
                                                <div>
                                                    <span className={`inline-block px-2 py-0.5 border text-[10px] font-mono uppercase ${OUTCOME_COLOR.promised_payment}`}>
                                                        {formatDate(n.promised_date)}
                                                    </span>
                                                    {n.promised_amount ? (
                                                        <div className="font-mono-num text-brand-green mt-1">
                                                            ₺ {formatMoney(n.promised_amount)}
                                                        </div>
                                                    ) : null}
                                                </div>
                                            ) : n ? (
                                                <span className={`inline-block px-2 py-0.5 border text-[10px] font-mono uppercase ${OUTCOME_COLOR[n.call_outcome] || OUTCOME_COLOR.other}`}>
                                                    {OUTCOME_LABELS[n.call_outcome] || "—"}
                                                </span>
                                            ) : <span className="text-neutral-400">—</span>}
                                        </td>
                                    </tr>
                                    {isOpen && (
                                        <tr className="bg-neutral-50 border-b border-black/5">
                                            <td colSpan={7} className="px-10 py-5">
                                                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                                                    <MonthlyBreakdown customerId={r.customer_id} />
                                                    <QuickNoteForm
                                                        customerId={r.customer_id}
                                                        onSaved={() => { setExpandedId(null); load(); }}
                                                        onCancel={() => setExpandedId(null)}
                                                    />
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function StatCell({ label, value, tone, testid }) {
    const color = tone === "debit" ? "text-brand-red" : tone === "credit" ? "text-brand-green" : "text-brand-ink";
    return (
        <div className="px-10 py-5 border-r border-black/10 last:border-r-0" data-testid={testid}>
            <div className="overline">{label}</div>
            <div className={`font-mono-num font-display text-3xl font-bold mt-2 ${color}`}>{value}</div>
        </div>
    );
}

const MONTH_LABEL_TR = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];

function MonthlyBreakdown({ customerId }) {
    const [data, setData] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    const [detail, setDetail] = React.useState(null); // {year, month, date_type}
    const scrollRef = React.useRef(null);

    React.useEffect(() => {
        let cancelled = false;
        setLoading(true);
        api.get(`/customers/${customerId}/monthly-breakdown`)
            .then((r) => { if (!cancelled) setData(r.data); })
            .catch(() => {})
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [customerId]);

    // Auto-scroll to the first non-zero month
    React.useEffect(() => {
        if (!data || !scrollRef.current) return;
        const firstIdx = data.by_issue.findIndex((v, i) => v > 0 || data.by_due_open[i] > 0);
        if (firstIdx > 0) {
            // Each th ≈ 68px; scroll left to bring firstIdx into view with 2-column pad
            scrollRef.current.scrollLeft = Math.max(0, (firstIdx - 2) * 68);
        }
    }, [data]);

    if (loading) {
        return <div className="text-xs font-mono text-neutral-400">Aylık analiz yükleniyor…</div>;
    }
    if (!data || !data.months?.length) {
        return <div className="text-xs font-mono text-neutral-400">Aylık veri yok.</div>;
    }
    if ((data.record_count || 0) === 0) {
        return (
            <div className="text-xs font-mono text-neutral-500 border border-dashed border-black/20 p-4">
                Bu müşteri için <a href="/acik-faturalar" className="text-brand-blue underline">Açık Faturalar</a> ekranından yüklenmiş kayıt yok.
            </div>
        );
    }

    const totalIssue = data.by_issue.reduce((a, b) => a + b, 0);
    const totalDueOpen = data.by_due_open.reduce((a, b) => a + b, 0);

    return (
        <div data-testid="monthly-breakdown">
            <div className="overline mb-2 flex items-center justify-between gap-2">
                <span>Ay Ay Borç Analizi</span>
                <span className="text-[10px] text-neutral-500 font-mono normal-case">
                    Açık Faturalar · {data.record_count || 0} kayıt
                </span>
            </div>
            <div ref={scrollRef} className="overflow-x-auto">
            <table className="text-[11px] font-mono border border-black/10 min-w-full">
                <thead>
                    <tr className="bg-neutral-100">
                        <th className="border-r border-black/10 px-2 py-1.5 text-left">Kategori</th>
                        {data.months.map((m, i) => (
                            <th key={i} className="border-r border-black/10 px-2 py-1.5 text-right whitespace-nowrap">
                                {MONTH_LABEL_TR[m.month - 1]} {String(m.year).slice(2)}
                            </th>
                        ))}
                        <th className="px-2 py-1.5 text-right bg-black text-white">Toplam</th>
                    </tr>
                </thead>
                <tbody>
                    <tr className="border-t border-black/10">
                        <td className="border-r border-black/10 px-2 py-1.5 whitespace-nowrap">
                            <span className="inline-block px-1 mr-1 text-amber-700">📄</span>
                            Fatura Ayı
                        </td>
                        {data.by_issue.map((v, i) => (
                            <td
                                key={i}
                                className={`border-r border-black/5 px-2 py-1.5 text-right ${v > 0 ? "bg-amber-50 text-amber-900 font-semibold cursor-pointer hover:bg-amber-200" : "text-neutral-300"}`}
                                onClick={v > 0 ? () => setDetail({ year: data.months[i].year, month: data.months[i].month, date_type: "issue" }) : undefined}
                                data-testid={v > 0 ? `mb-cell-issue-${data.months[i].year}-${data.months[i].month}` : undefined}
                            >
                                {v > 0 ? formatMoney(v) : "—"}
                            </td>
                        ))}
                        <td className="px-2 py-1.5 text-right font-bold bg-amber-100 text-amber-900">{formatMoney(totalIssue)}</td>
                    </tr>
                    <tr className="border-t border-black/10">
                        <td className="border-r border-black/10 px-2 py-1.5 whitespace-nowrap">
                            <span className="inline-block px-1 mr-1 text-brand-red">⏰</span>
                            Vade Ayı (Açık)
                        </td>
                        {data.by_due_open.map((v, i) => (
                            <td
                                key={i}
                                className={`border-r border-black/5 px-2 py-1.5 text-right ${v > 0 ? "bg-red-50 text-brand-red font-semibold cursor-pointer hover:bg-red-200" : "text-neutral-300"}`}
                                onClick={v > 0 ? () => setDetail({ year: data.months[i].year, month: data.months[i].month, date_type: "due" }) : undefined}
                                data-testid={v > 0 ? `mb-cell-due-${data.months[i].year}-${data.months[i].month}` : undefined}
                            >
                                {v > 0 ? formatMoney(v) : "—"}
                            </td>
                        ))}
                        <td className="px-2 py-1.5 text-right font-bold bg-red-100 text-brand-red">{formatMoney(totalDueOpen)}</td>
                    </tr>
                </tbody>
            </table>
            </div>
            <div className="text-[10px] font-mono text-neutral-500 mt-2 leading-relaxed">
                📄 <strong>Fatura Ayı</strong>: açık faturaların fatura tarihine göre toplamı (opsiyonel kolon) ·
                ⏰ <strong>Vade Ayı</strong>: açık faturaların vade tarihine göre toplamı (kalan tutar).
                Kaynak: <a href="/acik-faturalar" className="text-brand-blue underline">Açık Faturalar</a> ekranından yüklenen Excel.
                <span className="block mt-1 text-neutral-400">💡 Bir hücreye tıklayarak o aya ait açık faturaları görün.</span>
            </div>
            {detail && (
                <MonthDetailModal
                    customerId={customerId}
                    year={detail.year}
                    month={detail.month}
                    dateType={detail.date_type}
                    onClose={() => setDetail(null)}
                />
            )}
        </div>
    );
}

const MONTH_FULL_TR = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];

function MonthDetailModal({ customerId, year, month, dateType, onClose }) {
    const [data, setData] = React.useState(null);
    const [loading, setLoading] = React.useState(true);

    React.useEffect(() => {
        let cancelled = false;
        setLoading(true);
        api.get(`/customers/${customerId}/open-invoices-by-month?year=${year}&month=${month}&date_type=${dateType}`)
            .then((r) => { if (!cancelled) setData(r.data); })
            .catch(() => {})
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [customerId, year, month, dateType]);

    const label = dateType === "issue" ? "Fatura Tarihi" : "Vade Tarihi";
    const tone = dateType === "issue" ? "text-amber-700" : "text-brand-red";

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6" onClick={onClose}>
            <div
                data-testid="month-detail-modal"
                onClick={(e) => e.stopPropagation()}
                className="bg-white border border-black/15 w-full max-w-3xl max-h-[85vh] flex flex-col animate-fade-up"
            >
                <div className="px-6 py-4 border-b border-black/10 flex items-center justify-between">
                    <div>
                        <div className={`overline ${tone}`}>{label}</div>
                        <h3 className="font-display text-xl font-bold tracking-tight">
                            {MONTH_FULL_TR[month - 1]} {year} — Açık Faturalar
                        </h3>
                        {data && (
                            <div className="text-xs font-mono text-neutral-500 mt-1">
                                {data.customer_code} · {data.customer_name} · {data.count} kayıt · Toplam ₺ {formatMoney(data.total_amount)}
                            </div>
                        )}
                    </div>
                    <button onClick={onClose} className="text-neutral-500 hover:text-black" data-testid="mdm-close">
                        <X size={18} />
                    </button>
                </div>
                <div className="flex-1 overflow-y-auto p-6">
                    {loading && <div className="text-xs font-mono text-neutral-400">Yükleniyor…</div>}
                    {!loading && data && data.items.length === 0 && (
                        <div className="text-xs font-mono text-neutral-400">Kayıt yok.</div>
                    )}
                    {!loading && data && data.items.length > 0 && (
                        <table className="w-full text-sm font-mono">
                            <thead>
                                <tr className="border-b-2 border-black/20">
                                    <th className="text-left py-2 pr-3 overline">Fatura No</th>
                                    <th className="text-left py-2 pr-3 overline">Fatura Tarihi</th>
                                    <th className="text-left py-2 pr-3 overline">Vade Tarihi</th>
                                    <th className="text-right py-2 pr-3 overline">Tutar (₺)</th>
                                    <th className="text-left py-2 overline">Açıklama</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.items.map((it) => (
                                    <tr key={it.id} className="border-b border-black/5 hover:bg-neutral-50">
                                        <td className="py-2 pr-3 font-semibold">{it.invoice_no || "—"}</td>
                                        <td className="py-2 pr-3 text-neutral-600">{formatIsoDate(it.issue_date)}</td>
                                        <td className="py-2 pr-3 text-neutral-600">{formatIsoDate(it.due_date)}</td>
                                        <td className="py-2 pr-3 text-right font-semibold text-brand-red">
                                            {formatMoney(it.amount)}
                                        </td>
                                        <td className="py-2 text-xs text-neutral-500">{it.description || "—"}</td>
                                    </tr>
                                ))}
                                <tr className="border-t-2 border-black/40 bg-neutral-50">
                                    <td colSpan={3} className="py-2 pr-3 text-right font-bold overline">Toplam</td>
                                    <td className="py-2 pr-3 text-right font-bold text-brand-red text-base">
                                        ₺ {formatMoney(data.total_amount)}
                                    </td>
                                    <td></td>
                                </tr>
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
}

function formatIsoDate(iso) {
    if (!iso || iso.length < 10) return "—";
    const [y, m, d] = iso.slice(0, 10).split("-");
    return `${d}.${m}.${y}`;
}

function QuickNoteForm({ customerId, onSaved, onCancel }) {
    const [form, setForm] = useState({
        contact_person: "",
        call_outcome: "reached",
        body: "",
        promised_date: "",
        promised_amount: "",
    });
    const [saving, setSaving] = useState(false);

    async function submit(e) {
        e.preventDefault();
        if (!form.body.trim()) {
            toast.error("Not metni zorunlu");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                customer_id: customerId,
                body: form.body,
                call_outcome: form.call_outcome,
                contact_person: form.contact_person || "",
                promised_date: form.call_outcome === "promised_payment" && form.promised_date ? form.promised_date : null,
                promised_amount: form.call_outcome === "promised_payment" && form.promised_amount ? parseFloat(form.promised_amount) : null,
            };
            await api.post("/notes", payload);
            toast.success("Not kaydedildi");
            onSaved();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    }

    return (
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-6 gap-4" data-testid="quick-note-form">
            <div className="md:col-span-6 overline">Hızlı Görüşme Notu</div>
            <label className="md:col-span-2 block">
                <span className="overline block mb-1">Görüşülen Kişi</span>
                <input
                    data-testid="qn-contact"
                    value={form.contact_person}
                    onChange={(e) => setForm({ ...form, contact_person: e.target.value })}
                    placeholder="Ali Yılmaz"
                    className="w-full border border-black/20 px-3 py-2 text-sm font-mono bg-white focus:outline-none focus:border-brand-blue placeholder:text-neutral-400"
                />
            </label>
            <label className="md:col-span-2 block">
                <span className="overline block mb-1">Sonuç</span>
                <select
                    data-testid="qn-outcome"
                    value={form.call_outcome}
                    onChange={(e) => setForm({ ...form, call_outcome: e.target.value })}
                    className="w-full border border-black/20 px-3 py-2 text-sm font-mono bg-white focus:outline-none focus:border-brand-blue"
                >
                    {Object.entries(OUTCOME_LABELS).map(([k, v]) => (
                        <option key={k} value={k}>{v}</option>
                    ))}
                </select>
            </label>
            {form.call_outcome === "promised_payment" && (
                <>
                    <label className="md:col-span-1 block">
                        <span className="overline block mb-1">Söz Tarihi</span>
                        <input
                            data-testid="qn-promised-date"
                            type="date"
                            value={form.promised_date}
                            onChange={(e) => setForm({ ...form, promised_date: e.target.value })}
                            className="w-full border border-black/20 px-3 py-2 text-sm font-mono bg-white focus:outline-none focus:border-brand-blue"
                        />
                    </label>
                    <label className="md:col-span-1 block">
                        <span className="overline block mb-1">Söz Tutarı (₺)</span>
                        <input
                            data-testid="qn-promised-amount"
                            type="number"
                            step="0.01"
                            value={form.promised_amount}
                            onChange={(e) => setForm({ ...form, promised_amount: e.target.value })}
                            className="w-full border border-black/20 px-3 py-2 text-sm font-mono bg-white focus:outline-none focus:border-brand-blue"
                        />
                    </label>
                </>
            )}
            <label className="md:col-span-6 block">
                <span className="overline block mb-1">Not *</span>
                <textarea
                    data-testid="qn-body"
                    required
                    rows={2}
                    value={form.body}
                    onChange={(e) => setForm({ ...form, body: e.target.value })}
                    placeholder="Görüşme özeti…"
                    className="w-full border border-black/20 px-3 py-2 text-sm font-mono bg-white focus:outline-none focus:border-brand-blue placeholder:text-neutral-400"
                />
            </label>
            <div className="md:col-span-6 flex justify-end gap-2">
                <button type="button" onClick={onCancel} className="btn-outline" data-testid="qn-cancel">
                    <X size={13} /> Vazgeç
                </button>
                <button data-testid="qn-submit" className="btn-sharp" disabled={saving}>
                    <Save size={13} /> {saving ? "…" : "Kaydet"}
                </button>
            </div>
        </form>
    );
}
